import { BlockBlobClient } from "@azure/storage-blob";
import { getAuthToken } from "../hooks/useAuth";

const FUNCTION_URL = process.env.REACT_APP_AZURE_FUNCTION_URL || "";
const GET_FILES_URL = process.env.REACT_APP_GET_FILES_URL || "";
const GET_STORAGE_INFO_URL = process.env.REACT_APP_GET_STORAGE_INFO_URL || "";

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Custom error classes ──────────────────────────────────────────────────────

export class RateLimitError extends Error {
  resetTime: Date;
  limit: number;

  constructor(message: string, resetTime: Date, limit: number) {
    super(message);
    this.name = 'RateLimitError';
    this.resetTime = resetTime;
    this.limit = limit;
  }
}

export class StorageLimitError extends Error {
  usedGB: number;
  limitGB: number;
  storageInfo: StorageInfo;

  constructor(message: string, storageInfo: StorageInfo) {
    super(message);
    this.name = 'StorageLimitError';
    this.usedGB = storageInfo.usedGB;
    this.limitGB = storageInfo.limitGB;
    this.storageInfo = storageInfo;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StorageInfo {
  usedBytes: number;
  usedGB: number;
  limitGB: number;
  limitBytes: number;
  planName?: string;
  maxFileSizeMB?: number;
  usagePercent: number;
  nearLimit: boolean;    // 80%+
  urgentLimit: boolean;  // 95%+
  atLimit: boolean;      // 100%
}

// ── Response handler ──────────────────────────────────────────────────────────

async function handleApiResponse(response: Response): Promise<any> {
  // Handle rate limit (429)
  if (response.status === 429) {
    try {
      const data = await response.json();
      const resetTime = data.resetTime ? new Date(data.resetTime) : new Date(Date.now() + 60 * 60 * 1000);
      const limit = data.limit || 100;
      throw new RateLimitError(
        data.message || 'Rate limit exceeded. Please try again later.',
        resetTime,
        limit
      );
    } catch (err) {
      if (err instanceof RateLimitError) throw err;
      throw new RateLimitError(
        'Too many requests. Please try again later.',
        new Date(Date.now() + 60 * 60 * 1000),
        100
      );
    }
  }

  // Handle storage limit (403)
  if (response.status === 403) {
    try {
      const data = await response.json();
      if (data.error === 'storage_limit_exceeded') {
        throw new StorageLimitError(
          data.message || 'Storage limit reached. Delete some files to continue uploading.',
          data.storageInfo
        );
      }
    } catch (err) {
      if (err instanceof StorageLimitError) throw err;
    }
  }

  // Handle other error status codes
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        errorMessage = data.error || data.message || errorMessage;
      } else {
        const text = await response.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          errorMessage = `Server error (${response.status}). Please try again.`;
        } else {
          errorMessage = text.substring(0, 200);
        }
      }
    } catch (parseError) {
      errorMessage = `Request failed with status ${response.status}`;
    }

    throw new Error(errorMessage);
  }

  // Success — parse and return JSON
  try {
    return await response.json();
  } catch (err) {
    throw new Error('Invalid response from server');
  }
}

// ── getSasUrl ─────────────────────────────────────────────────────────────────

export async function getSasUrl(
  filename: string,
  userId: string,
  folder?: string,
  fileSize?: number
): Promise<{ sasUrl: string; storageInfo?: StorageInfo }> {
  if (!userId) throw new Error('User ID is required');
  if (!filename) throw new Error('Filename is required');

  try {
    // Pass filename and folder separately — backend constructs the blobName
    const params = new URLSearchParams({
      filename: encodeURIComponent(filename),
      userId: encodeURIComponent(userId),
    });
    if (fileSize !== undefined) params.append('fileSize', fileSize.toString());
    if (folder) params.append('folder', folder);

    const response = await fetch(`${FUNCTION_URL}&${params.toString()}`, {
      method: 'GET',
      headers: await authHeaders(),
    });

    const data = await handleApiResponse(response);

    if (!data.sasUrl) throw new Error('No SAS URL returned from server');

    return {
      sasUrl: data.sasUrl,
      storageInfo: data.storageInfo,
    };
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof StorageLimitError) throw error;
    if (error instanceof Error) throw new Error(`Failed to get upload URL: ${error.message}`);
    throw new Error('Failed to get upload URL');
  }
}

// ── uploadFileToAzure ─────────────────────────────────────────────────────────

export interface UploadResult {
  success: boolean;
  storageInfo?: StorageInfo;
}

export async function uploadFileToAzure(
  file: File,
  userId: string,
  folder: string = '',
  onProgress?: (progress: number) => void
): Promise<UploadResult> {
  if (!file) throw new Error('No file provided');
  if (!userId) throw new Error('User ID is required');

  try {
    const { sasUrl, storageInfo } = await getSasUrl(file.name, userId, folder, file.size);

    const blockBlobClient = new BlockBlobClient(sasUrl);

    await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: {
        blobContentType: file.type,
      },
      onProgress: (ev) => {
        if (onProgress && file.size > 0) {
          const percent = Math.round((ev.loadedBytes / file.size) * 100);
          onProgress(percent);
        }
      },
    });

    return { success: true, storageInfo };

  } catch (error) {
    if (error instanceof RateLimitError) {
      const resetTimeStr = error.resetTime.toLocaleTimeString();
      throw new RateLimitError(
        `Upload limit reached (${error.limit} uploads/hour). Try again after ${resetTimeStr}.`,
        error.resetTime,
        error.limit
      );
    }

    if (error instanceof StorageLimitError) throw error;

    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }

    throw new Error('Upload failed. Please try again.');
  }
}

// ── getUserFiles ──────────────────────────────────────────────────────────────

export async function getUserFiles(userId: string) {
  if (!userId) throw new Error('User ID is required');

  try {
    const response = await fetch(
      `${GET_FILES_URL}&userId=${encodeURIComponent(userId)}`,
      { method: 'GET', headers: await authHeaders() }
    );

    const data = await handleApiResponse(response);

    return Array.isArray(data) ? data : (data.files || []);
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof Error) throw new Error(`Failed to load files: ${error.message}`);
    throw new Error('Failed to load files');
  }
}
